import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { Btn, Field, Input, Select, Spinner, Th, cellClip,
         useSortableColumns, useColumnWidths } from "../components/Shared";

/**
 * This group's prices, one row per line item, filterable.
 *
 * Before this a group admin could import a price list and see how many rules
 * landed, and nothing else — the numbers every quotation is drawn from were
 * legible only in the CSV they arrived in.
 *
 * Three filters and no more, confirmed with the requester: Service, Affiliation
 * and Price. Speculative ones (date range, price type) are cheap to add and
 * expensive to remove once somebody relies on them.
 */

const ACCESSORS = {
  service:     r => r.service,
  affiliation: r => r.affiliation || "",
  price:       r => Number(r.price ?? 0),
  valid_from:  r => r.valid_from || "",
};

export default function PriceTable({ groupId }) {
  const [rows, setRows]   = useState(null);
  const [error, setError] = useState(null);
  const [service, setService] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  useEffect(() => {
    if (!groupId) return;
    let live = true;
    setRows(null);
    setError(null);
    const q = new URLSearchParams();
    if (service) q.set("service", service);
    if (affiliation) q.set("affiliation", affiliation);
    if (priceMin !== "") q.set("price_min", priceMin);
    if (priceMax !== "") q.set("price_max", priceMax);
    const suffix = q.toString() ? `?${q}` : "";
    api.get(`/api/groups/${groupId}/price-rules${suffix}`)
      .then(r => live && setRows(r))
      .catch(e => live && setError(e.message || "Could not load prices"));
    return () => { live = false; };
  }, [groupId, service, affiliation, priceMin, priceMax]);

  // The pickers are built from what is actually priced, so a filter can never
  // select nothing. They come from the UNFILTERED first load — narrowing the
  // options as you narrow the rows would strand you with no way back.
  const [options, setOptions] = useState({ services: [], affiliations: [] });
  useEffect(() => {
    if (!groupId) return;
    let live = true;
    api.get(`/api/groups/${groupId}/price-rules`)
      .then(all => live && setOptions({
        services: [...new Set(all.map(r => r.service))].sort(),
        affiliations: [...new Set(all.map(r => r.affiliation).filter(Boolean))].sort(),
      }))
      .catch(() => {});
    return () => { live = false; };
  }, [groupId]);

  const { sorted, sortKey, sortDir, toggleSort } = useSortableColumns(rows || [], ACCESSORS);
  const { widths, setWidth, totalWidth } = useColumnWidths({
    service: 240, affiliation: 150, price: 100, period: 180, list: 110,
  });
  const filtered = useMemo(() => service || affiliation || priceMin || priceMax,
                           [service, affiliation, priceMin, priceMax]);

  if (error) return <p style={{ margin: 0, fontSize: 13, color: "#b91c1c" }}>{error}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end",
                    marginBottom: 12 }}>
        <div style={{ minWidth: 200 }}>
          <Field label="Service">
            <Select value={service} onChange={e => setService(e.target.value)}
                    options={[{ value: "", label: "Any service" },
                              ...options.services.map(s => ({ value: s, label: s }))]} />
          </Field>
        </div>
        <div style={{ minWidth: 170 }}>
          <Field label="Affiliation">
            <Select value={affiliation} onChange={e => setAffiliation(e.target.value)}
                    options={[{ value: "", label: "Any affiliation" },
                              ...options.affiliations.map(a => ({ value: a, label: a }))]} />
          </Field>
        </div>
        <div style={{ width: 110 }}>
          <Field label="Price from">
            <Input type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} />
          </Field>
        </div>
        <div style={{ width: 110 }}>
          <Field label="Price to">
            <Input type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} />
          </Field>
        </div>
        {filtered && (
          <Btn variant="ghost"
               onClick={() => { setService(""); setAffiliation(""); setPriceMin(""); setPriceMax(""); }}>
            Clear
          </Btn>
        )}
      </div>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
          {filtered
            ? "No prices match these filters."
            : "This group has no prices yet. Import a price list above."}
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>
            {rows.length} price{rows.length === 1 ? "" : "s"}
          </p>
          {/* Cards on a phone, not a scroll box: each price is a record of its
              own, found by the filters above rather than by reading a column
              down, and the sort still orders the cards. */}
          <table className="responsive-table"
                 style={{ borderCollapse: "collapse", tableLayout: "fixed", width: totalWidth }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                <Th label="Service" sortKey="service" activeKey={sortKey} dir={sortDir}
                    onSort={toggleSort} width={widths.service} onWidth={w => setWidth("service", w)} />
                <Th label="Affiliation" sortKey="affiliation" activeKey={sortKey} dir={sortDir}
                    onSort={toggleSort} width={widths.affiliation} onWidth={w => setWidth("affiliation", w)} />
                <Th label="Price" align="right" sortKey="price" activeKey={sortKey} dir={sortDir}
                    onSort={toggleSort} width={widths.price} onWidth={w => setWidth("price", w)} />
                <Th label="Valid" sortKey="valid_from" activeKey={sortKey} dir={sortDir}
                    onSort={toggleSort} width={widths.period} onWidth={w => setWidth("period", w)} />
                <Th label="List" width={widths.list} onWidth={w => setWidth("list", w)} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td data-label="Service" style={{ padding: "8px 12px", fontSize: 13, ...cellClip }} title={r.service}>
                    {r.service}
                  </td>
                  <td data-label="Affiliation" style={{ padding: "8px 12px", fontSize: 13, color: "#374151" }}
                      title={r.affiliation_label || ""}>
                    {/* PPMS's wildcard is stored as NULL: a price that applies
                        whatever the customer is. Saying "any" is the truth;
                        showing a dash reads as missing data. */}
                    {r.affiliation || <span style={{ color: "#6b7280" }}>any</span>}
                  </td>
                  <td data-label="Price" style={{ padding: "8px 12px", fontSize: 13, textAlign: "right",
                               fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.price ?? "—"}
                  </td>
                  <td data-label="Valid" style={{ padding: "8px 12px", fontSize: 12, color: "#6b7280" }}>
                    {r.valid_from || "—"}{r.valid_to ? ` → ${r.valid_to}` : ""}
                  </td>
                  <td data-label="List" style={{ padding: "8px 12px", fontSize: 12, color: "#6b7280", ...cellClip }}>
                    {r.price_list}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
